import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../styles.css';

const API_URL = window.location.protocol === 'https:'
  ? 'https://www.siriusmusicgroup.com/api/s3/songs'
  : 'http://localhost:3030/api/s3/songs';

const artists = ['jeff', 'smuggla', 'wyse'];

const LandingPage = () => {
  const [songs, setSongs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const [wakeLock, setWakeLock] = useState(null);
  const [autoPlay, setAutoPlay] = useState(true);
  const audioRef = useRef(null);

  // 🔹 Fetch all songs from all artists
  useEffect(() => {
    const fetchAllSongs = async () => {
      let allSongs = [];
      for (let artist of artists) {
        try {
          const response = await axios.get(`${API_URL}/${artist}`);
          if (response.data.songs && response.data.songs.length > 0) {
            allSongs = [...allSongs, ...response.data.songs];
          }
        } catch (error) {
          console.error(`❌ Error fetching songs for ${artist}:`, error);
        }
      }
      setSongs(allSongs);
    };
    fetchAllSongs();
  }, []);

  // 🔹 Audio management system
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const handleCanPlay = () => {
      if (autoPlay) {
        audioElement.play()
          .then(() => setIsPlaying(true))
          .catch(error => {
            console.error('Autoplay failed:', error);
            setIsPlaying(false);
          })
          .finally(() => setAutoPlay(false));
      }
    };

    audioElement.addEventListener('canplay', handleCanPlay);
    audioElement.load();

    return () => {
      audioElement.removeEventListener('canplay', handleCanPlay);
    };
  }, [currentIndex, autoPlay]);

  // 🔹 Play/pause synchronization
  useEffect(() => {
    if (!audioRef.current) return;
    isPlaying ? audioRef.current.play() : audioRef.current.pause();
  }, [isPlaying]);

  // 🔹 Wake Lock management
  useEffect(() => {
    let wakeLockInstance = null;

    const requestWakeLock = async () => {
      if ('wakeLock' in navigator && isPlaying) {
        try {
          wakeLockInstance = await navigator.wakeLock.request('screen');
          setWakeLock(wakeLockInstance);
          wakeLockInstance.addEventListener('release', () => {
            console.log('Wake Lock was released');
          });
        } catch (err) {
          console.error(`${err.name}, ${err.message}`);
        }
      }
    };

    const releaseWakeLock = () => {
      if (wakeLockInstance) {
        wakeLockInstance.release().then(() => setWakeLock(null));
      }
    };

    if (isPlaying) requestWakeLock();
    return () => releaseWakeLock();
  }, [isPlaying]);

  // 🔹 Visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isPlaying && audioRef.current) {
        audioRef.current.play().catch(console.error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPlaying]);

  // 🔹 Progress monitoring
  useEffect(() => {
    const handleTimeUpdate = () => {
      if (!audioRef.current) return;
      const { currentTime, duration } = audioRef.current;
      if (currentTime >= duration - 2) playNextSong();
    };

    audioRef.current?.addEventListener('timeupdate', handleTimeUpdate);
    return () => audioRef.current?.removeEventListener('timeupdate', handleTimeUpdate);
  }, [currentIndex, songs, isShuffling]);

  // 🔹 Track navigation
  const playNextSong = () => {
    if (!songs.length) return;
    const nextIndex = isShuffling 
      ? Math.floor(Math.random() * songs.length)
      : (currentIndex + 1) % songs.length;
    setCurrentIndex(nextIndex);
    setAutoPlay(true);
  };

  const playPreviousSong = () => {
    if (!songs.length) return;
    const prevIndex = isShuffling
      ? Math.floor(Math.random() * songs.length)
      : currentIndex === 0 ? songs.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setAutoPlay(true);
  };

  // 🔹 UI handlers
  const togglePlayPause = () => setIsPlaying(!isPlaying);
  const toggleShuffle = () => setIsShuffling(!isShuffling);

  return (
    <div className="landing-container">
      <h1 className="landing-title">🎵 Welcome to <span className="highlight">Sirius Music</span></h1>
      <h2 className="landing-subtitle">Your Gateway to the Best in Music & Sound Production</h2>
      <p className="landing-description">
        Discover top artists, exclusive releases, and the ultimate music experience.
      </p>

      <div className="radio-container">
        <h3 className="text-warning">🎧 Sirius Radio</h3>
        <p className="now-playing">
          {songs.length > 0 ? `Now Playing: ${songs[currentIndex]?.name}` : "No songs available"}
        </p>

        <div className="button-group">
          <button
            className={`btn btn-${isPlaying ? 'danger' : 'warning'} m-2`}
            onClick={togglePlayPause}
          >
            {isPlaying ? "Pause" : "Play"}
          </button>
          <button
            className={`btn ${isShuffling ? "btn-success" : "btn-secondary"} m-2`}
            onClick={toggleShuffle}
          >
            Shuffle: {isShuffling ? "On" : "Off"}
          </button>
          <button className="btn btn-warning m-2" onClick={playPreviousSong}>
            Previous
          </button>
          <button className="btn btn-warning m-2" onClick={playNextSong}>
            Next
          </button>
        </div>
      </div>

      <audio
        ref={audioRef}
        key={songs[currentIndex]?.url}
        onEnded={playNextSong}
        playsInline
        webkit-playsinline="true"
        preload="metadata"
      >
        {songs[currentIndex]?.url && (
          <source src={songs[currentIndex].url} type="audio/mp3" />
        )}
      </audio>
    </div>
  );
};

export default LandingPage;